import React, { useMemo, useRef, useState, useEffect } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import CalendarIcon from "app/assets/IconComponent/CalendarIcon";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import AvatarWithName from "v2/common/AvatarWithName";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PerformanceLedgerRow {
  id: string;
  shareholderName: string;
  avatarUrl?: string;
  initials: string;
  avatarColor: string;
  ownership: number;
  value: number;
  trueIqScore?: number;
  life: {
    coverage: number;
    status: "SURPLUS" | "DEFICIT";
    difference: number;
    trueIqIndex?: number;
  };
  disability: {
    coverage: number;
    status: "SURPLUS" | "DEFICIT";
    difference: number;
    trueIqIndex?: number;
  };
}

interface PerformanceLedgerProps {
  rows?: PerformanceLedgerRow[];
  projectionRows?: PerformanceLedgerRow[];
  selectedYear?: number;
  totalYears?: number;
  isDark?: boolean;
  showProtect?: boolean;
  showProjections?: boolean;
  lifeSummaryTrueIqIndex?: number;
  disabilitySummaryTrueIqIndex?: number;
  onYearChange?: (year: number) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtM = (v: number): string => {
  if (v === 0) return "$0.0M";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) {
    return `$${(v / 1_000_000).toLocaleString("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    })}M`;
  }
  if (abs >= 1_000) return `$${(v / 1_000).toLocaleString("en-US", { maximumFractionDigits: 1 })}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_ROWS: PerformanceLedgerRow[] = [
  {
    id: "1",
    shareholderName: "Stark, Tony",
    initials: "TS",
    avatarColor: "#4F46E5",
    ownership: 60,
    value: 7_320_000,
    trueIqScore: 107,
    life: { coverage: 10_200_000, status: "SURPLUS", difference: 2_880_000, trueIqIndex: 110 },
    disability: { coverage: 0, status: "DEFICIT", difference: 7_320_000, trueIqIndex: 0 },
  },
  {
    id: "2",
    shareholderName: "Smith, Rachel",
    initials: "SR",
    avatarColor: "#7C3AED",
    ownership: 40,
    value: 7_320_000,
    trueIqScore: 109,
    life: { coverage: 10_200_000, status: "SURPLUS", difference: 2_880_000, trueIqIndex: 110 },
    disability: { coverage: 1_000_000, status: "DEFICIT", difference: 6_320_000, trueIqIndex: 10 },
  },
  {
    id: "3",
    shareholderName: "Smith, Rachel",
    initials: "SR",
    avatarColor: "#7C3AED",
    ownership: 40,
    value: 7_320_000,
    trueIqScore: 60,
    life: { coverage: 6_000_000, status: "DEFICIT", difference: 1_320_000, trueIqIndex: 10 },
    disability: { coverage: 4_000_000, status: "DEFICIT", difference: 3_320_000, trueIqIndex: 10 },
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const RowBadge = ({
  coverage,
  status,
  difference,
  trueIqIndex,
}: {
  coverage: number;
  status: "SURPLUS" | "DEFICIT";
  difference: number;
  trueIqIndex?: number;
}) => {
  const isGreen = status === "SURPLUS";
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        style={{
          backgroundColor: isGreen ? "#F0FDF4" : "#FFF1F2",
          border: `1px solid ${isGreen ? "#BBF7D0" : "#FECACA"}`,
          borderRadius: 8,
          padding: "6px 10px",
          minWidth: 148,
          display: "inline-block",
        }}
      >
        <p
          style={{
            color: isGreen ? "#15803D" : "#B91C1C",
            fontWeight: 700,
            fontSize: 15,
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {fmtM(coverage)}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: isGreen ? "#22C55E" : "#EF4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isGreen ? (
              <ArrowUp size={10} color="white" strokeWidth={3} />
            ) : (
              <ArrowDown size={10} color="white" strokeWidth={3} />
            )}
          </div>
          <span
            style={{
              color: isGreen ? "#15803D" : "#B91C1C",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {status} {fmtM(difference)}
          </span>
        </div>
      </div>
      {trueIqIndex !== undefined && (
        <span
          style={{
            position: "absolute",
            top: "50%",
            right: -16,
            transform: "translateY(-50%)",
            backgroundColor: isGreen ? "#F0FDF4" : "#FFF1F2",
            border: `1px solid ${isGreen ? "#BBF7D0" : "#FECACA"}`,
            color: isGreen ? "#15803D" : "#B91C1C",
            fontSize: 9,
            fontWeight: 700,
            padding: "3px 5px",
            borderRadius: 4,
            whiteSpace: "nowrap",
            zIndex: 1,
          }}
        >
          {trueIqIndex}%
        </span>
      )}
    </div>
  );
};

const SummaryBadge = ({
  status,
  amount,
  trueIqIndex,
}: {
  status: "SURPLUS" | "DEFICIT";
  amount: number;
  trueIqIndex?: number;
}) => {
  const isGreen = status === "SURPLUS";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      <div
        style={{
          backgroundColor: isGreen ? "#F0FDF4" : "#FFF1F2",
          border: `1px solid ${isGreen ? "#BBF7D0" : "#FECACA"}`,
          borderRadius: 10,
          padding: "8px 14px",
          minWidth: 148,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            backgroundColor: isGreen ? "#22C55E" : "#EF4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isGreen ? (
            <ArrowUp size={18} color="white" strokeWidth={3} />
          ) : (
            <ArrowDown size={18} color="white" strokeWidth={3} />
          )}
        </div>
        <div>
          <p
            style={{
              color: isGreen ? "#15803D" : "#B91C1C",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              margin: 0,
              letterSpacing: "0.05em",
            }}
          >
            {status}
          </p>
          <p
            style={{
              color: isGreen ? "#15803D" : "#B91C1C",
              fontSize: 18,
              fontWeight: 800,
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            {fmtM(amount)}
          </p>
        </div>
      </div>
      {trueIqIndex !== undefined && (
        <span
          style={{
            alignSelf: "center",
            backgroundColor: isGreen ? "#F0FDF4" : "#FFF1F2",
            border: `1px solid ${isGreen ? "#BBF7D0" : "#FECACA"}`,
            color: isGreen ? "#15803D" : "#B91C1C",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 4,
          }}
        >
          {trueIqIndex}%
        </span>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const HEADER_CLS = "text-[11px] font-semibold uppercase tracking-wider text-gray-400 text-left";
const CELL_CLS = "py-3";

export default function PerformanceLedger({
  rows,
  projectionRows,
  selectedYear = 1,
  totalYears = 30,
  isDark = false,
  showProtect = false,
  showProjections = false,
  lifeSummaryTrueIqIndex,
  disabilitySummaryTrueIqIndex,
  onYearChange,
}: PerformanceLedgerProps) {
  const data = rows && rows.length > 0 ? rows : MOCK_ROWS;
  const projData = projectionRows && projectionRows.length > 0 ? projectionRows : MOCK_ROWS;
  const [showYearPicker, setShowYearPicker] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowYearPicker(false);
      }
    };
    if (showYearPicker) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showYearPicker]);

  const handleYearSelect = (year: number) => {
    setShowYearPicker(false);
    if (onYearChange) {
      onYearChange(year);
    } else {
      window.dispatchEvent(new CustomEvent("ircSelectionChanged", { detail: { year } }));
    }
  };

  const summary = useMemo(() => {
    const lifeCoverage = data.reduce((s, r) => s + r.life.coverage, 0);
    const disabilityCoverage = data.reduce((s, r) => s + r.disability.coverage, 0);
    const lifeNet = data.reduce(
      (s, r) => s + (r.life.status === "SURPLUS" ? r.life.difference : -r.life.difference),
      0,
    );
    const disabilityNet = data.reduce(
      (s, r) =>
        s +
        (r.disability.status === "SURPLUS" ? r.disability.difference : -r.disability.difference),
      0,
    );
    const lifeIqRows = data.filter((r) => r.life.trueIqIndex !== undefined);
    const disabilityIqRows = data.filter((r) => r.disability.trueIqIndex !== undefined);
    const lifeAvgIqIndex =
      lifeIqRows.length > 0
        ? Math.round(lifeIqRows.reduce((s, r) => s + r.life.trueIqIndex!, 0) / lifeIqRows.length)
        : undefined;
    const disabilityAvgIqIndex =
      disabilityIqRows.length > 0
        ? Math.round(
            disabilityIqRows.reduce((s, r) => s + r.disability.trueIqIndex!, 0) /
              disabilityIqRows.length,
          )
        : undefined;
    return {
      lifeCoverage,
      lifeStatus: lifeNet >= 0 ? ("SURPLUS" as const) : ("DEFICIT" as const),
      lifeDiff: Math.abs(lifeNet),
      disabilityCoverage,
      disabilityStatus: disabilityNet >= 0 ? ("SURPLUS" as const) : ("DEFICIT" as const),
      disabilityDiff: Math.abs(disabilityNet),
      lifeAvgIqIndex,
      disabilityAvgIqIndex,
    };
  }, [data]);

  const projSummary = useMemo(() => {
    const lifeCoverage = projData.reduce((s, r) => s + r.life.coverage, 0);
    const disabilityCoverage = projData.reduce((s, r) => s + r.disability.coverage, 0);
    const lifeNet = projData.reduce(
      (s, r) => s + (r.life.status === "SURPLUS" ? r.life.difference : -r.life.difference),
      0,
    );
    const disabilityNet = projData.reduce(
      (s, r) =>
        s +
        (r.disability.status === "SURPLUS" ? r.disability.difference : -r.disability.difference),
      0,
    );
    return {
      lifeCoverage,
      lifeStatus: lifeNet >= 0 ? ("SURPLUS" as const) : ("DEFICIT" as const),
      lifeDiff: Math.abs(lifeNet),
      disabilityCoverage,
      disabilityStatus: disabilityNet >= 0 ? ("SURPLUS" as const) : ("DEFICIT" as const),
      disabilityDiff: Math.abs(disabilityNet),
    };
  }, [projData]);

  const PROJ_BG = "#EEF6FF";
  const projYear = selectedYear + 1;

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const valueText = isDark ? "text-gray-200" : "text-[#191A1C]";
  const subText = isDark ? "text-gray-400" : "text-[#55556D]";
  const dividerColor = isDark ? "#3A3A50" : "#F3F4F6";

  return (
    <div className={`${cardBg} px-5 pt-4 pb-3`}>
      <>
        {/* ── Table (year bar baked in as thead row) ─────────────────── */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: showProjections ? 1080 : 640,
            }}
          >
            <thead>
              {/* ── Year bar row ──────────────────────────────────────── */}
              <tr>
                <td />
                <td
                  colSpan={4}
                  style={{ paddingBottom: 12, paddingRight: showProjections ? 8 : 0 }}
                >
                  <div
                    style={{
                      backgroundColor: "#004781",
                      borderRadius: 8,
                      padding: "10px 0",
                      textAlign: "center",
                    }}
                  >
                    <span
                      className="conthrax-font"
                      style={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: 16,
                        letterSpacing: "0.12em",
                      }}
                    >
                      YR{selectedYear}
                    </span>
                  </div>
                </td>
                {showProjections && (
                  <td colSpan={4} style={{ paddingBottom: 12, paddingLeft: 8 }}>
                    <div
                      style={{
                        border: "2px dashed #3B82F6",
                        borderRadius: 8,
                        padding: "9px 0",
                        textAlign: "center",
                        backgroundColor: PROJ_BG,
                      }}
                    >
                      <span
                        className="conthrax-font"
                        style={{
                          color: "#3B82F6",
                          fontWeight: 700,
                          fontSize: 16,
                          letterSpacing: "0.12em",
                        }}
                      >
                        YR{projYear}
                      </span>
                    </div>
                  </td>
                )}
              </tr>

              {/* ── Column headers ───────────────────────────────────── */}
              <tr style={{ borderBottom: `1px solid ${dividerColor}` }}>
                <th
                  className={`${HEADER_CLS} pb-[10px] pr-4 min-w-[180px] sticky left-0 z-[2] ${cardBg}`}
                >
                  Shareholder
                </th>
                <th className={`${HEADER_CLS} pb-[10px] pr-3 min-w-[80px]`}>Ownership</th>
                <th className={`${HEADER_CLS} pb-[10px] pr-5 min-w-[80px]`}>Value</th>
                <th className={`${HEADER_CLS} pb-[10px] pr-7 min-w-[160px]`}>Life</th>
                <th className={`${HEADER_CLS} pb-[10px]`}>Disability</th>
                {showProjections && (
                  <>
                    <th className={`${HEADER_CLS} pb-[10px] pr-3 pl-4 min-w-[80px]`}>Ownership</th>
                    <th className={`${HEADER_CLS} pb-[10px] pr-5 min-w-[80px]`}>Value</th>
                    <th className={`${HEADER_CLS} pb-[10px] pr-7 min-w-[160px]`}>Life</th>
                    <th className={`${HEADER_CLS} pb-[10px] min-w-[160px]`}>Disability</th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {data.map((row, i) => {
                const projRow = projData[i] ?? row;
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: i < data.length - 1 ? `1px solid ${dividerColor}` : "none",
                    }}
                  >
                    {/* Shareholder — sticky */}
                    <td
                      className={CELL_CLS}
                      style={{
                        paddingRight: 16,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        background: isDark ? "#2A2A3D" : "#ffffff",
                      }}
                    >
                      <div>
                        <AvatarWithName
                          name={row.shareholderName}
                          imageUrl={row.avatarUrl}
                          initial={row.initials}
                          avatarSize={36}
                          avatarStyle={{
                            backgroundColor: row.avatarColor,
                            color: "white",
                            fontSize: "12px",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                          nameClassName={`font-semibold text-[13px] ${valueText}`}
                        />
                        {showProtect && row.trueIqScore !== undefined && (
                          <div className="flex items-center gap-1 mt-0.5 pl-[46px]">
                            <TrueIqIcon
                              width={12}
                              height={12}
                              fill={row.trueIqScore >= 100 ? "#22C55E" : "#EF4444"}
                            />
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: row.trueIqScore >= 100 ? "#22C55E" : "#EF4444",
                              }}
                            >
                              {row.trueIqScore.toFixed(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Current year */}
                    <td
                      className={`${CELL_CLS} font-semibold text-[13px] ${subText}`}
                      style={{ paddingRight: 12 }}
                    >
                      {row.ownership}%
                    </td>
                    <td
                      className={`${CELL_CLS} font-semibold text-[13px] ${valueText}`}
                      style={{ paddingRight: 20 }}
                    >
                      {fmtM(row.value)}
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: showProtect ? 28 : 12 }}>
                      <RowBadge
                        coverage={row.life.coverage}
                        status={row.life.status}
                        difference={row.life.difference}
                        trueIqIndex={showProtect ? row.life.trueIqIndex : undefined}
                      />
                    </td>
                    <td className={CELL_CLS} style={{ paddingRight: showProtect ? 28 : 16 }}>
                      <RowBadge
                        coverage={row.disability.coverage}
                        status={row.disability.status}
                        difference={row.disability.difference}
                        trueIqIndex={showProtect ? row.disability.trueIqIndex : undefined}
                      />
                    </td>
                    {/* Projection year */}
                    {showProjections && (
                      <>
                        <td
                          className={`${CELL_CLS} font-semibold text-[13px] ${subText}`}
                          style={{ paddingRight: 12, paddingLeft: 12, backgroundColor: PROJ_BG }}
                        >
                          {projRow.ownership}%
                        </td>
                        <td
                          className={`${CELL_CLS} font-semibold text-[13px] ${valueText}`}
                          style={{ paddingRight: 20, backgroundColor: PROJ_BG }}
                        >
                          {fmtM(projRow.value)}
                        </td>
                        <td
                          className={CELL_CLS}
                          style={{
                            paddingRight: showProtect ? 28 : 12,
                            paddingLeft: 4,
                            backgroundColor: PROJ_BG,
                          }}
                        >
                          <RowBadge
                            coverage={projRow.life.coverage}
                            status={projRow.life.status}
                            difference={projRow.life.difference}
                            trueIqIndex={showProtect ? projRow.life.trueIqIndex : undefined}
                          />
                        </td>
                        <td
                          className={CELL_CLS}
                          style={{ paddingRight: showProtect ? 28 : 8, backgroundColor: PROJ_BG }}
                        >
                          <RowBadge
                            coverage={projRow.disability.coverage}
                            status={projRow.disability.status}
                            difference={projRow.disability.difference}
                            trueIqIndex={showProtect ? projRow.disability.trueIqIndex : undefined}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>

            {/* ── Footer ─────────────────────────────────────────────── */}
            <tfoot>
              <tr style={{ borderTop: `1px solid ${dividerColor}` }}>
                {/* Calendar */}
                <td colSpan={3} className="pt-3 pb-2">
                  <div ref={calendarRef} style={{ position: "relative", display: "inline-block" }}>
                    <button
                      type="button"
                      onClick={() => setShowYearPicker((v) => !v)}
                      style={{
                        width: 42,
                        height: 42,
                        border: `1.5px solid ${showYearPicker ? "#004781" : "#D1D5DB"}`,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "white",
                        cursor: "pointer",
                      }}
                    >
                      <CalendarIcon
                        fill={showYearPicker ? "#004781" : "#9CA3AF"}
                        width={22}
                        height={22}
                      />
                    </button>
                    {showYearPicker && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "110%",
                          left: 0,
                          background: isDark ? "#1E1E2E" : "#ffffff",
                          border: `1px solid ${isDark ? "#3A3A50" : "#E5E7EB"}`,
                          borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          padding: "8px 4px",
                          zIndex: 50,
                          maxHeight: 220,
                          overflowY: "auto",
                          minWidth: 80,
                        }}
                      >
                        {Array.from({ length: totalYears }, (_, i) => i + 1).map((yr) => (
                          <button
                            type="button"
                            key={yr}
                            onClick={() => handleYearSelect(yr)}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: "6px 14px",
                              textAlign: "left",
                              background: yr === selectedYear ? "#004781" : "transparent",
                              color:
                                yr === selectedYear ? "#ffffff" : isDark ? "#e2e8f0" : "#191A1C",
                              fontWeight: yr === selectedYear ? 700 : 400,
                              fontSize: 13,
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            YR{yr}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>

                {/* Current year — Life */}
                <td className="pt-3 pb-2" style={{ paddingRight: 12, verticalAlign: "top" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#004781" }}>
                      Coverage: {fmtM(summary.lifeCoverage)}
                    </span>
                    <SummaryBadge
                      status={summary.lifeStatus}
                      amount={summary.lifeDiff}
                      trueIqIndex={
                        showProtect ? lifeSummaryTrueIqIndex ?? summary.lifeAvgIqIndex : undefined
                      }
                    />
                  </div>
                </td>
                {/* Current year — Disability */}
                <td className="pt-3 pb-2" style={{ verticalAlign: "top" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#004781" }}>
                      Coverage: {fmtM(summary.disabilityCoverage)}
                    </span>
                    <SummaryBadge
                      status={summary.disabilityStatus}
                      amount={summary.disabilityDiff}
                      trueIqIndex={
                        showProtect
                          ? disabilitySummaryTrueIqIndex ?? summary.disabilityAvgIqIndex
                          : undefined
                      }
                    />
                  </div>
                </td>

                {/* Projection year summary */}
                {showProjections && (
                  <>
                    <td
                      colSpan={2}
                      className="pt-3 pb-2"
                      style={{ verticalAlign: "top", backgroundColor: PROJ_BG }}
                    />
                    <td
                      className="pt-3 pb-2"
                      style={{
                        paddingRight: 12,
                        verticalAlign: "top",
                        backgroundColor: PROJ_BG,
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#3B82F6" }}>
                          Coverage: {fmtM(projSummary.lifeCoverage)}
                        </span>
                        <SummaryBadge
                          status={projSummary.lifeStatus}
                          amount={projSummary.lifeDiff}
                        />
                      </div>
                    </td>
                    <td
                      className="pt-3 pb-2"
                      style={{ verticalAlign: "top", backgroundColor: PROJ_BG }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#3B82F6" }}>
                          Coverage: {fmtM(projSummary.disabilityCoverage)}
                        </span>
                        <SummaryBadge
                          status={projSummary.disabilityStatus}
                          amount={projSummary.disabilityDiff}
                        />
                      </div>
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </>
    </div>
  );
}
